import { REQUEST_STATUS_OPTIONS, TABLES } from '@/utils/constants';
import { getRecord, insertRecord } from '@/utils/helpers/data';
import { REQUEST_COLUMNS, Request } from '@/utils/schema';
import { supabase } from '@/utils/supabase';

export type { Request };
export const COLUMNS = REQUEST_COLUMNS;

/**
 * Creates a new request record in the database.
 * @param request - The request data to insert.
 * @returns The created request or null if insertion failed.
 * @throws Will throw an error if the insertion fails.
 */
export const createRequest = async (
  request: Request
): Promise<Request | null> => {
  if (!Object.values(REQUEST_STATUS_OPTIONS).includes(request.status))
    return null;

  return insertRecord<Request>(TABLES.REQUESTS, request);
};

/**
 * Retrieves a single request by its ID.
 * @param requestId - The ID of the request to retrieve.
 * @returns The requested record or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequest = async (
  requestId: string | null | undefined
): Promise<Request | null> => {
  if (!requestId) return null;

  return getRecord<Request>(TABLES.REQUESTS, COLUMNS.ID, requestId);
};

/**
 * Retrieves a single request by its code.
 * @param code - The code of the request to retrieve.
 * @returns The requested record or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequestByCode = async (
  code: string | null | undefined
): Promise<Request | null> => {
  if (!code) return null;

  return getRecord<Request>(TABLES.REQUESTS, COLUMNS.CODE, code);
};

/**
 * Retrieves all future appointments for a specific dentist including approved, pending, and rescheduled status.
 * Only returns id, date, start_time, and end_time fields.
 * @param dentistId - The ID of the dentist.
 * @returns An array of future appointment times with limited fields.
 * @throws Will throw an error if the retrieval fails.
 */
export const getDentistFutureAppointmentsAndRequests = async (
  dentistId: string | null | undefined
): Promise<Pick<Request, 'id' | 'date' | 'start_time' | 'end_time'>[]> => {
  if (!dentistId) return [];

  const { data, error } = await supabase.rpc(
    'get_dentist_future_appointments_and_requests',
    {
      p_dentist_id: dentistId
    }
  );

  if (error) throw new Error(error.message);

  return data ?? [];
};
